import {
  type SortParam,
  formatSortParam,
  nextSortParamValue,
  parseSortParam,
  setFilterParams
} from '@/utils/list-filter-params';
import type { GridSortModel } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router';

// REQ-7/REQ-8 refactor pass (executor, check 2: no logic exists twice): CustomersPage, OrdersPage
// and SubscriptionsPage each read their own sort param from the URL and wrote the identical
// setSearchParams/setFilterParams/nextSortParamValue triple back on a DataGrid sort change,
// differing only in which field list and default they closed over. One hook instead of three
// copies, the same consolidation use-debounced-filter-param.ts already did for the search-box
// debounce these same three pages share.
export const useSortParam = <T extends string>(
  key: string,
  pageKey: string,
  fields: readonly [T, ...T[]],
  defaultValue: SortParam<T>
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortModel = parseSortParam(searchParams, key, fields, defaultValue);
  const sortParam = formatSortParam(sortModel);

  const handleSortModelChange = (model: GridSortModel) => {
    const nextValue = nextSortParamValue(model[0], fields, defaultValue);
    // Writing the value already in effect would still reset the page and re-render the grid. The
    // DataGrid reports an empty model for a sort field that is not one of its columns (Customers'
    // default `createdAt`), so without this check that report, the write and the re-render loop
    // forever, pinning the table to page 0.
    if (nextValue === sortParam) return;
    setSearchParams(prev => setFilterParams(prev, { [key]: nextValue }, pageKey), {
      replace: true
    });
  };

  return { sortModel, sortParam, handleSortModelChange };
};
